import { useState, useEffect, useRef } from "react";
import { lists } from "./Series_List_Data";
import Series_List_Set from "./Series_List_Set";
import { sortLeftRightTopBottom } from "../../../refractCommon/miro/sortLeftRightTopBottom";
import ToolWrapper from "../../PowerPack_Common/ToolWrapper";

const { board } = window.miro;
const buffer = 300;

const toolConfig = {
  tool_name: "Select a Series",
  tool_icon: "icon-cursor-user",
  tool_short_description: "",
  tool_id: "SelectASeries",
  works_on: "many to one",

  tool_type_icons: (
    <div className="cs5 ce12 grid">
      <div className="m2 icon icon-rectangle cs1"></div>
      <div className="m2 icon icon-sticky-bulk cs2"></div>
      <div className="m2 icon icon-text cs3"></div>
    </div>
  ),
  tool_is_in_uat: false,
  tool_is_pro: false,
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}

function SelectASeries({
  boardSelection,
  userIsPro,
  environment,
  selectionArrayThatWillBeFilledWithSeries,
}) {
  const [toolOpen, setToolOpen] = useState(false);
  const [copies, setCopies] = useState(1);
  const [duplicateSelectionArray, setDuplicateSelectionArray] = useState([]);
  const [fillWithListSelectionArray, setFillWithListSelectionArray] = useState(
    []
  );

  const content = useRef(null);

  useEffect(() => {
    runSelectionItemsForDuplicate(boardSelection);
    runSelectItemsForFillWithList(boardSelection);
  }, [boardSelection]);

  // Fill with list item type icons
  let fillWithListItemTypeIcons = (
    <div className="cs5 ce12 grid mt-0 mb-0">
      <div className="m2 icon icon-rectangle cs1"></div>
      <div className="m2 icon icon-sticky-bulk cs2"></div>
      <div className="m2 icon icon-card cs3"></div>
      <div className="m2 icon icon-frame cs4"></div>
      <div className="m2 icon icon-text cs5"></div>
    </div>
  );

  function handleToggleOpen() {
    setToolOpen(!toolOpen);
  }

  //DUPLICATE
  function runSelectionItemsForDuplicate(selection) {
    if (!selection) {
      return [];
    }
    if (selection.length === 0) {
      return selection;
    }
    if (selection.length > 0) {
      let filteredSelection = selection.filter(
        (item) =>
          item.type === "sticky_note" ||
          item.type === "shape" ||
          item.type === "text" ||
          item.type === "card" ||
          item.type === "frame"
      );
      setDuplicateSelectionArray(filteredSelection);
    }
  }

  // FILL WITH LIST
  async function runSelectItemsForFillWithList(selection) {
    if (!selection) {
      return [];
    }
    if (selection.length === 0) {
      return selection;
    }
    if (selection.length > 0) {
      let reorderedList = await sortLeftRightTopBottom(selection);

      let filteredSelection = reorderedList.filter(
        (item) =>
          item.type === "sticky_note" ||
          item.type === "shape" ||
          item.type === "text" ||
          item.type === "card" ||
          item.type === "frame"
      );
      setFillWithListSelectionArray(filteredSelection);
      return filteredSelection;
    }
  }

  async function fillSelectionWithList(listObject) {
    let selection = await board.getSelection();
    let fillWithListSelectionArray = await runSelectItemsForFillWithList(
      selection
    );
    let filteredSelection = fillWithListSelectionArray;

    filteredSelection.forEach((item, index) => {
      if (["shape", "sticky_note", "text"].includes(item.type)) {
        if (index < listObject.content.length) {
          item.content = listObject.content[index];
        } else {
          item.content = "";
        }
        syncItem(item);
      }
      if (["card", "frame"].includes(item.type)) {
        if (index < listObject.content.length) {
          item.title = listObject.content[index];
        } else {
          item.content = "";
        }
        syncItem(item);
      }
    });
  }

  /* ---------------------------------------------------------- */

  // FUNCTION TO MAKE AN ENTIRE SERIES INTO YELLOW STICKY NOTES
  async function createWholeSeries(listObject) {
    let selection = await board.getSelection();
    let filteredSelection = await runSelectItemsForFillWithList(selection);
    let itemToCopy = filteredSelection[0];

    // let newItemsFilledReadyForLayout = [];

    const { x, y, width, height } = await board.viewport.get();
    const centerX = x + width * 0.5;
    const centerY = y + height * 0.5;

    let marginX = 0.35;
    let xOffset;
    let newX;
    let itemWidth = itemToCopy.width;
    let index = 0;

    if (filteredSelection.length === 1) {
      for (let listEntry of listObject.content) {
        xOffset = itemWidth * index + marginX * index;
        newX = itemToCopy.x + xOffset;
        index += 1;

        if (itemToCopy.type === "sticky_note") {
          let newItem = await buildSticky({
            content: listEntry,
            style: itemToCopy.style,
            x: newX,
            y: itemToCopy.y + itemToCopy.height,
            width: itemToCopy.width,
          });
          newItem.sync();
        }
        if (itemToCopy.type === "shape") {
          let newItem = await buildShape({
            content: listEntry,
            shape: itemToCopy.shape,
            style: itemToCopy.style,
            x: newX,
            y: itemToCopy.y + itemToCopy.height,
            width: itemToCopy.width,
            height: itemToCopy.height,
          });
          newItem.sync();
        }
        if (itemToCopy.type === "frame") {
          let newItem = await buildFrame({
            title: listEntry,
            style: itemToCopy.style,
            x: newX,
            y: itemToCopy.y + itemToCopy.height,
            width: itemToCopy.width,
            height: itemToCopy.height,
          });
          newItem.sync();
        }
        if (itemToCopy.type === "card") {
          let newItem = await buildCard({
            content: listEntry,
            style: itemToCopy.style,
            x: newX,
            y: itemToCopy.y + itemToCopy.height,
            width: itemToCopy.width,
          });
          newItem.sync();
        }
        if (itemToCopy.type === "text") {
          let newItem = await buildText({
            content: listEntry,
            style: itemToCopy.style,
            x: newX,
            y: itemToCopy.y + itemToCopy.height,
            width: itemToCopy.width,
          });
          newItem.sync();
        }
      }
    }
  }

  /* ---------------------------------------------------------- */

  // Function called when a property of a board item has changed
  async function syncItem(miroItem) {
    const updatedItem = await miroItem.sync();
    return updatedItem;
  }
  /* ---------------------------------------------------------- */

  /* ---------------------------------------------------------- */
  // Duplicate sticky note, card, and shape
  async function runDuplicateSelection() {
    let filteredSelection = duplicateSelectionArray;

    if (filteredSelection.length === 1) {
      duplicateItem(filteredSelection[0]);
    }
  }

  /* ---------------------------------------------------------- */
  // Async functions for creating individual items
  async function buildSticky(sticky) {
    const newSticky = await board.createStickyNote(sticky);
    return newSticky;
  }

  async function buildShape(shape) {
    const newShape = await board.createShape(shape);
    return newShape;
  }

  async function buildText(text) {
    const newText = await board.createText(text);
    return newText;
  }

  async function buildCard(card) {
    const newCard = await board.createCard(card);
    return newCard;
  }

  async function buildFrame(card) {
    const newFrame = await board.createFrame(card);
    return newFrame;
  }

  /* ---------------------------------------------------------- */

  function duplicateItem(item) {
    let i = 0; //iterator
    // Do while statements refactored into a seperate methods?
    let returnItemsArray = [];
    switch (item.type) {
      case "sticky_note":
        i = copies;

        do {
          let newItem = buildSticky({
            style: item.style,
            x: item.x,
            y: item.y + item.height,
            width: item.width,
          });
          returnItemsArray.push(newItem);
          i--;
        } while (i > 0);
        setCopies(0);
        break;
      case "shape":
        i = copies;
        do {
          let newItem = buildShape({
            style: item.style,
            shape: item.shape,
            x: item.x,
            y: item.y + item.height,
            width: item.width,
            height: item.height,
          });
          returnItemsArray.push(newItem);
          i--;
        } while (i > 0);
        setCopies(0);
        return returnItemsArray;

      case "card":
        i = copies;
        do {
          let newItem = buildCard({
            style: item.style,
            x: item.x,
            y: item.y + item.height,
            width: item.width,
          });
          returnItemsArray.push(newItem);
          i--;
        } while (i > 0);
        setCopies(0);
        return returnItemsArray;
      case "text":
        i = copies;
        do {
          let newItem = buildText({
            style: item.style,
            x: item.x,
            y: item.y + item.height,
            width: item.width,
            // height: item.height,
          });
          returnItemsArray.push(newItem);
          i--;
        } while (i > 0);
        setCopies(0);
        return returnItemsArray;
      case "frame":
        i = copies;
        do {
          let newItem = buildFrame({
            style: item.style,
            x: item.x,
            y: item.y + item.height,
            width: item.width,
            height: item.height,
          });
          returnItemsArray.push(newItem);
          i--;
        } while (i > 0);
        setCopies(0);
        return returnItemsArray;

      default:
        board.alert("Sorry, that selection can not be duplicated.");
        return;
    }
  }

  /* ---------------------------------------------------------- */
  // N U M B E R  S E Q U E N C E
  // BUILD / ADD
  async function buildNewNumberSequence() {}

  // FILL
  async function fillSelectionWithNewNumberSequence() {}

  /* ---------------------------------------------------------- */
  // Dynamically add buttons based on each of the keys in the
  // list object
  function createListButtonSections() {
    const listsKeys = Object.keys(lists);

    return listsKeys.map((list_group_key, index) => {
      return (
        <Series_List_Set
          key={list_group_key}
          listGroupObj={lists[list_group_key]}
          fillSelectionWithList={fillSelectionWithList}
          createWholeSeries={createWholeSeries}
          fillWithListSelectionArray={fillWithListSelectionArray}
          duplicateSelectionArray={duplicateSelectionArray}
        />
      );
    });
  }

  return (
    <ToolWrapper
      toolConfig={toolConfig}
      userIsPro={userIsPro}
      environment={environment}
    >
        <div id="accordion-margin" className="cs1 ce12 grid mb-0">
          <div className="grid cs1 ce12">
            <p className="p-medium cs1 ce12 mt-0 mb-0">
              Select 1 or more items to <b>"Fill"</b> or... <br /> Select a
              single item, press
              <b>"All"</b> & the whole series fills duplicates of that item.
              <i>
                <br />
                <b>Note:</b> Some styles are not yet supported... you may have
                to style your items after the 'all' build.
              </i>
            </p>
            <div
              id="series-works-on"
              className="help-container grid cs1 ce12 mt-0 mb-8 pt-8">
              <p className="p-small cs1 ce4 mb-0 mt-0">
                Works on <b>many:</b>
              </p>

              {fillWithListItemTypeIcons}

              <p
                className="p-medium cs1 ce12 align-center mb-0 mt-0 "
                style={{
                  color:
                    selectionArrayThatWillBeFilledWithSeries.length < 1
                      ? "var(--red900)"
                      : "black",
                }}>
                <b>{selectionArrayThatWillBeFilledWithSeries.length} </b>item
                <>
                  {selectionArrayThatWillBeFilledWithSeries.length === 0 ||
                  selectionArrayThatWillBeFilledWithSeries.length > 1
                    ? "s"
                    : ""}
                </>{" "}
                currently selected
              </p>
            </div>
          </div>

          <div className="cs1 ce12">{createListButtonSections()}</div>
        </div>
    </ToolWrapper>
  );
}

export default SelectASeries;
